import { Link } from 'react-router-dom'
import {
  ArrowUpRight,
  GitBranch,
  Package,
  Workflow,
  Sparkles,
  Settings2,
} from 'lucide-react'
import { GithubLinkButton, PageHeader } from '@loop3/ui'
import type { ComponentType, SVGProps } from 'react'
import {
  DISTRIBUTION,
  REPOS,
  type DistributionCategory,
} from '@/lib/distribution'

type IconType = ComponentType<SVGProps<SVGSVGElement>>

type CategoryKey = DistributionCategory | 'repos' | 'packages'

// Keep in sync with platform/app/src/pages/PackagesPage.tsx
const PACKAGES_COUNT = 3

const CATEGORIES: {
  key: CategoryKey
  title: string
  subtitle: string
  icon: IconType
  to: string
}[] = [
  {
    key: 'repos' as const,
    title: 'Repos',
    subtitle:
      'Every GitHub repo in the sync distribution, linked to its client.',
    icon: GitBranch,
    to: '/l3os-center/repos',
  },
  {
    key: 'skills',
    title: 'Skills',
    subtitle: 'Claude Code skills available when working in managed repos.',
    icon: Sparkles,
    to: '/l3os-center/skills',
  },
  {
    key: 'workflows',
    title: 'Workflows',
    subtitle: 'GitHub Actions automations synced to every managed repo.',
    icon: Workflow,
    to: '/l3os-center/workflows',
  },
  {
    key: 'configs',
    title: 'Configs',
    subtitle: 'Shared settings, ignorelists, and git hooks.',
    icon: Settings2,
    to: '/l3os-center/configs',
  },
  {
    key: 'packages' as const,
    title: 'Packages',
    subtitle: 'Shared workspace packages consumed by every Loop3 app.',
    icon: Package,
    to: '/l3os-center/packages',
  },
]

export function DistributionPage() {
  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        eyebrow="L3OS Sync"
        title="What Loop3 distributes"
        subtitle="L3OS Sync keeps every Loop3-managed repo on the same Claude Code skills, GitHub workflows, and configs. Change something here once and it reaches every client repo, so each team starts with the same tooling and standards."
        actions={
          <GithubLinkButton
            href="https://github.com/Loop3Studio/l3-os"
            label="View repository"
          />
        }
      />

      <section>
        <div className="grid grid-cols-1 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((cat) => {
            const count =
              cat.key === 'repos'
                ? REPOS.length
                : cat.key === 'packages'
                  ? PACKAGES_COUNT
                  : DISTRIBUTION[cat.key as DistributionCategory].length
            const Icon = cat.icon
            return (
              <Link
                key={cat.key}
                to={cat.to}
                className="group flex flex-col justify-between p-4 min-h-[120px] border-b sm:border-b-0 sm:[&:not(:first-child)]:border-l border-border/60 hover:bg-[var(--l3-violet-soft)] transition-colors"
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="size-4 opacity-60 group-hover:opacity-100 group-hover:text-[var(--l3-violet)] transition-colors" />
                    <h4 className="text-sm font-medium group-hover:text-[var(--l3-violet)] transition-colors">
                      {cat.title}
                    </h4>
                    <ArrowUpRight className="size-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                  </div>
                  <p className="text-xs opacity-60 leading-relaxed">
                    {cat.subtitle}
                  </p>
                </div>
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.12em] opacity-40 mt-3">
                  {count}{' '}
                  {cat.key === 'repos'
                    ? count === 1
                      ? 'repo'
                      : 'repos'
                    : cat.key === 'packages'
                      ? count === 1
                        ? 'package'
                        : 'packages'
                      : count === 1
                        ? 'file'
                        : 'files'}
                </span>
              </Link>
            )
          })}
        </div>
      </section>
    </div>
  )
}
